import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, Camera, Plus } from "lucide-react";
import { Wizard, WField, WNote } from "@/components/fs/Wizard";
import { V } from "@/lib/vocab";

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const usd = (n) => (n === null || n === undefined || n === "" ? "—"
  : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

/* Wizard 3 — set up a recurring partner. Creating one of these is a COMMITMENT: it mints the
   recurring work order the visits hang off, so the money question can't be skipped. */
export default function RecurringVendorWizard({ tenantId, vendors, glCodes, onDone, onClose }) {
  const [f, setF] = useState({
    name: "", vendor_id: "", category: "common_area_cleaning", dow: [1, 3],
    start: "06:00", end: "10:00", photo_required: true, checklist: "", location: "",
    gl_code: "", amount: "",
  });
  const [busy, setBusy] = useState(false);
  const vendor = (vendors || []).find((v) => v.id === f.vendor_id);
  const checklist = f.checklist.split("\n").map((s) => s.trim()).filter(Boolean);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/field-services/schedules`, { ...f, checklist });
      const wo = data.work_order || {};
      toast.success((wo.paperwork_missing || []).length
        ? `Set up as ${wo.ref} — visits start once their paperwork lands`
        : `Set up as ${wo.ref} — charged to ${wo.gl_code || "the trade default"}`);
      onDone?.(); onClose?.();
    } catch (e) { toast.error(errMsg(e, "Could not save that")); }
    setBusy(false);
  };

  const steps = [
    {
      key: "who", title: "Who & what",
      hint: "One company can have several of these — a Tue/Thu full clean and a Mon–Sat garbage check are two different commitments.",
      blocked: !f.vendor_id ? "Pick the company that's coming."
        : !f.name.trim() ? "Give this a name you'd recognise on a list — “Garbage check”, “Full property clean”." : null,
      render: () => (
        <>
          <WField label="Company">
            <select data-testid="sched-vendor" value={f.vendor_id} onChange={(e) => setF({ ...f, vendor_id: e.target.value })}
              className="w-full rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm outline-none">
              <option value="">Pick a company…</option>
              {(vendors || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </WField>
          <WField label="What to call it">
            <input data-testid="sched-name" value={f.name} placeholder="e.g. Garbage check"
              onChange={(e) => setF({ ...f, name: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
          </WField>
          <WField label="Trade" hint={`This is also the router: ${V.a("person")}'s report in this trade lands on their next visit instead of pulling someone out.`}>
            <select data-testid="sched-cat" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
              className="w-full rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm outline-none">
              {Object.entries(CAT_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </WField>
          <WField label="Where on the property (optional)">
            <input data-testid="sched-location" value={f.location} placeholder="e.g. all common areas, pool deck"
              onChange={(e) => setF({ ...f, location: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
          </WField>
        </>
      ),
    },
    {
      key: "when", title: "Which days",
      hint: `The crew gets a text the morning of each visit with everything ${V.lcs("person")} reported since the last one.`,
      blocked: !f.dow.length ? "Pick at least one day." : null,
      render: () => (
        <>
          <div>
            <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">Days</div>
            <div className="flex flex-wrap gap-1">
              {DOW.map((d, di) => (
                <button key={d} data-testid={`sched-dow-${di}`} onClick={() => setF({
                  ...f, dow: f.dow.includes(di) ? f.dow.filter((x) => x !== di) : [...f.dow, di],
                })} className={`h-10 w-12 rounded-xl text-xs font-bold transition-colors ${f.dow.includes(di) ? "bg-ink text-white" : "bg-sand-100 text-ink/50 hover:bg-sand-200"}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <WField label="From">
              <input type="time" data-testid="sched-start" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })}
                className="rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
            </WField>
            <WField label="To">
              <input type="time" data-testid="sched-end" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })}
                className="rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
            </WField>
          </div>
        </>
      ),
    },
    {
      key: "proof", title: "Checklist & proof",
      hint: "The checklist is what the crew ticks off on their phone. Photos are what stops “we came” being the whole record.",
      render: () => (
        <>
          <WField label="Checklist — one per line (optional)">
            <textarea data-testid="sched-checklist" value={f.checklist} rows={6}
              placeholder={"Empty all bins\nSweep the breezeways\nHose the pool deck"}
              onChange={(e) => setF({ ...f, checklist: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
          </WField>
          <button data-testid="sched-photo" onClick={() => setF({ ...f, photo_required: !f.photo_required })}
            className={`flex w-full items-center justify-between gap-3 rounded-xl border p-3.5 text-left transition-colors ${f.photo_required ? "border-emerald-200 bg-emerald-50" : "border-sand-200 bg-white"}`}>
            <span className="flex items-center gap-2.5">
              <Camera className={`h-4 w-4 ${f.photo_required ? "text-emerald-600" : "text-ink/40"}`} />
              <span>
                <span className="block text-sm font-bold text-ink">Require a photo to close a visit</span>
                <span className="block text-xs text-ink/55">A visit marked done with no photo shows as “done, no proof”.</span>
              </span>
            </span>
            <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${f.photo_required ? "bg-emerald-600 text-white" : "bg-sand-200 text-ink/60"}`}>
              {f.photo_required ? "Required" : "Optional"}
            </span>
          </button>
        </>
      ),
    },
    {
      key: "money", title: "Budget line",
      hint: "This is a standing commitment to a company, so it carries the line it's charged to. Every invoice against it inherits this code — the vendor never sees it.",
      render: () => (
        <>
          <WField label="Charge it to" hint={(glCodes || []).length ? "Leave it blank to use the default for this trade." : "No codes yet — add them under Settings → Accounting codes. We'll fall back to the trade default."}>
            <select data-testid="sched-code" value={f.gl_code} onChange={(e) => setF({ ...f, gl_code: e.target.value })}
              className="w-full rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm outline-none">
              <option value="">Default for this trade</option>
              {(glCodes || []).map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
            </select>
          </WField>
          <WField label="Monthly or per-visit price (optional)">
            <input data-testid="sched-amount" type="number" min="0" step="0.01" value={f.amount}
              placeholder="e.g. 850" onChange={(e) => setF({ ...f, amount: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-3 py-2.5 text-sm outline-none" />
          </WField>
        </>
      ),
    },
    {
      key: "review", title: "Review",
      hint: "One last look. You can change any of it afterwards.",
      render: () => (
        <>
          <div className="rounded-xl border border-sand-200 bg-white p-4 text-sm" data-testid="sched-review">
            <div className="font-heading text-base font-bold text-ink">{f.name || "Recurring service"}</div>
            <div className="mt-1 text-xs text-ink/55">
              {vendor?.name || "—"} · {CAT_LABEL[f.category]}
              {f.location ? ` · ${f.location}` : ""}
            </div>
            <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink/60">
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="h-3.5 w-3.5" />
                {f.dow.slice().sort((a, b) => a - b).map((d) => DOW[d]).join(", ")} · {f.start}–{f.end}
              </span>
              <span>{checklist.length} checklist item{checklist.length === 1 ? "" : "s"}</span>
              <span>{f.photo_required ? "Photos required" : "Photos optional"}</span>
              <span>{f.gl_code || "trade default"}{f.amount ? ` · ${usd(f.amount)}` : ""}</span>
            </div>
          </div>
          <WNote testid="sched-review-note">
            Creating this opens a recurring work order. If their insurance isn't current the visits are
            created but paused — nobody's on the property until the certificate lands.
          </WNote>
        </>
      ),
    },
  ];

  return (
    <Wizard testid="wiz-recurring" steps={steps} busy={busy} onFinish={create}
      finishLabel="Set it up" finishIcon={Plus} />
  );
}
